import React from 'react';
import { fireEvent, render, screen } from '@testing-library/react';

import ConfigProvider from 'components/ConfigProvider';

import UserDetailsStep from './UserDetailsStep';
import { DETAILS_STEP, EXISTING_USER } from './copy';

const DRAFT = {
  email: '',
  firstName: '',
  jobRole: '',
  lastName: '',
  mobile: '',
  mobileCountryCode: '1',
  ssoProvider: '',
};

// PD-4684. What the server offers when the admin has the choice.
const AUTHENTICATION_OPTIONS = [
  { id: 'paccar', label: 'PCAR (Pass)' },
  { id: 'password', label: 'Password' },
];

const onBlurEmail = jest.fn();
const onBlurMobile = jest.fn();
const onChange = jest.fn();
const onResolveExisting = jest.fn();

const renderStep = (props = {}) =>
  render(
    <ConfigProvider>
      <UserDetailsStep
        draft={DRAFT}
        jobRoleOptions={[{ id: 'fleet_manager', label: 'Fleet Manager' }]}
        authenticationOptions={AUTHENTICATION_OPTIONS}
        isAskable
        phoneCountryCodeOptions={[{ id: '1', label: '+1' }]}
        onBlurEmail={onBlurEmail}
        onBlurMobile={onBlurMobile}
        onChange={onChange}
        onResolveExisting={onResolveExisting}
        {...props}
      />
    </ConfigProvider>,
  );

const EXISTING_USER_MATCH = {
  id: 42,
  fullName: 'Amanda Metz',
  jobRole: 'Fleet Manager',
  email: 'ametz@companyemail.com',
  mobile: '3505793153',
  mobileCountryCode: '1',
  restoreAccountIds: [7],
  addAccountIds: [],
};

beforeEach(() => {
  onBlurEmail.mockClear();
  onBlurMobile.mockClear();
  onChange.mockClear();
  onResolveExisting.mockClear();
});

describe('management/Users UserDetailsStep', () => {
  it('renders the fields the design asks for', () => {
    renderStep();

    [
      DETAILS_STEP.firstName,
      DETAILS_STEP.lastName,
      DETAILS_STEP.jobRole,
      DETAILS_STEP.email,
      DETAILS_STEP.countryCode,
      DETAILS_STEP.mobile,
      DETAILS_STEP.authentication,
    ].forEach((label) =>
      expect(screen.getByLabelText(label)).toBeInTheDocument(),
    );
  });

  // The design collects no username, no GPS fields and no external visibility,
  // even though the ticket's prose lists them. The username is derived server side.
  it('does not ask for a username', () => {
    renderStep();

    expect(screen.queryByLabelText(/username/i)).not.toBeInTheDocument();
  });

  it("carries the design's hints", () => {
    renderStep();

    expect(screen.getByText(DETAILS_STEP.jobRoleHelp)).toBeInTheDocument();
    expect(screen.getByText(DETAILS_STEP.mobileHelp)).toBeInTheDocument();
  });

  it('reports a typed field', () => {
    renderStep();

    fireEvent.change(screen.getByLabelText(DETAILS_STEP.firstName), {
      target: { value: 'Jane' },
    });

    expect(onChange).toHaveBeenCalledWith('firstName', 'Jane');
  });

  it('checks the email as the admin leaves it', () => {
    renderStep();

    fireEvent.blur(screen.getByLabelText(DETAILS_STEP.email));

    expect(onBlurEmail).toHaveBeenCalled();
  });

  it('checks the mobile as the admin leaves it', () => {
    renderStep();

    fireEvent.blur(screen.getByLabelText(DETAILS_STEP.mobile));

    expect(onBlurMobile).toHaveBeenCalled();
  });

  describe('duplicate warnings', () => {
    it('warns about a taken email', () => {
      renderStep({
        checks: { emailTaken: true, mobileTaken: false, mobileValid: true },
      });

      expect(screen.getByText(DETAILS_STEP.emailTaken)).toBeInTheDocument();
    });

    it('drops the plain message once a restore/add card can be shown instead', () => {
      renderStep({
        checks: {
          emailTaken: true,
          mobileTaken: false,
          mobileValid: true,
          existingUser: { ...EXISTING_USER_MATCH, scenario: 'restore' },
        },
      });

      expect(
        screen.queryByText(DETAILS_STEP.emailTaken),
      ).not.toBeInTheDocument();
    });

    it('warns about a taken phone number', () => {
      renderStep({
        checks: { emailTaken: false, mobileTaken: true, mobileValid: true },
      });

      expect(screen.getByText(DETAILS_STEP.mobileTaken)).toBeInTheDocument();
    });

    // The warning replaces the hint rather than stacking under it, as in the
    // mockup.
    it('drops the mobile hint while warning', () => {
      renderStep({
        checks: { emailTaken: false, mobileTaken: true, mobileValid: true },
      });

      expect(
        screen.queryByText(DETAILS_STEP.mobileHelp),
      ).not.toBeInTheDocument();
    });

    it('shows nothing when neither is taken', () => {
      renderStep();

      expect(
        screen.queryByText(DETAILS_STEP.emailTaken),
      ).not.toBeInTheDocument();
      expect(
        screen.queryByText(DETAILS_STEP.mobileTaken),
      ).not.toBeInTheDocument();
    });

    // Amber on both halves of the pair, as the mockup draws it: the country code
    // is as likely to be the wrong part as the number.
    it('marks the country code as well as the number', () => {
      renderStep({
        checks: { emailTaken: false, mobileTaken: true, mobileValid: true },
      });

      // The warning border is InputField's, so what is checkable here is that the
      // Select was told about it at all — jsdom computes no styles.
      expect(
        screen.getByLabelText(DETAILS_STEP.countryCode),
      ).toBeInTheDocument();
      expect(screen.getByText(DETAILS_STEP.mobileTaken)).toBeInTheDocument();
    });
  });

  describe('the existing-user card', () => {
    it('shows RESTORE ACCESS for the restore scenario', () => {
      renderStep({
        checks: {
          emailTaken: true,
          mobileTaken: false,
          mobileValid: true,
          existingUser: { ...EXISTING_USER_MATCH, scenario: 'restore' },
        },
      });

      expect(screen.getByText(EXISTING_USER.restore.title)).toBeInTheDocument();
      expect(
        screen.getByText(EXISTING_USER_MATCH.fullName, { exact: false }),
      ).toBeInTheDocument();
      expect(
        screen.getByRole('button', { name: EXISTING_USER.restore.action }),
      ).toBeInTheDocument();
    });

    it('shows the bare mobile number rather than a stray country code prefix', () => {
      renderStep({
        checks: {
          emailTaken: true,
          mobileTaken: false,
          mobileValid: true,
          existingUser: {
            ...EXISTING_USER_MATCH,
            mobileCountryCode: null,
            scenario: 'restore',
          },
        },
      });

      expect(
        screen.getByText(EXISTING_USER_MATCH.mobile, { exact: false }),
      ).toBeInTheDocument();
      expect(screen.queryByText(/null/i)).not.toBeInTheDocument();
    });

    it('shows ADD USER for the add scenario', () => {
      renderStep({
        checks: {
          emailTaken: true,
          mobileTaken: false,
          mobileValid: true,
          existingUser: {
            ...EXISTING_USER_MATCH,
            scenario: 'add',
            restoreAccountIds: [],
            addAccountIds: [7],
          },
        },
      });

      expect(screen.getByText(EXISTING_USER.add.title)).toBeInTheDocument();
      expect(
        screen.getByRole('button', { name: EXISTING_USER.add.action }),
      ).toBeInTheDocument();
    });

    it('calls onResolveExisting when the button is clicked', () => {
      renderStep({
        checks: {
          emailTaken: true,
          mobileTaken: false,
          mobileValid: true,
          existingUser: { ...EXISTING_USER_MATCH, scenario: 'restore' },
        },
      });

      fireEvent.click(
        screen.getByRole('button', { name: EXISTING_USER.restore.action }),
      );

      expect(onResolveExisting).toHaveBeenCalled();
    });

    it('shows no card for the acknowledge scenario', () => {
      renderStep({
        checks: {
          emailTaken: true,
          mobileTaken: false,
          mobileValid: true,
          existingUser: { ...EXISTING_USER_MATCH, scenario: 'acknowledge' },
        },
      });

      expect(
        screen.queryByText(EXISTING_USER.restore.title),
      ).not.toBeInTheDocument();
      expect(
        screen.queryByText(EXISTING_USER.add.title),
      ).not.toBeInTheDocument();
    });
  });

  // The mockup puts both at x=0 spanning the full row (Figma 787:76709,
  // 787:76731). Hanging them off the number field's own helpMessage indented them
  // 135px, under that column alone.
  describe('the phone row message', () => {
    /**
     * True when `message` belongs to the whole row rather than to one column.
     *
     * Walks up from the message to the first ancestor holding the number, and asks
     * whether that ancestor holds the country code too. Inside the number's own
     * column it would not — which is precisely the layout being guarded against.
     */
    const spansTheRow = (message) => {
      const number = screen.getByLabelText(DETAILS_STEP.mobile);
      const code = screen.getByLabelText(DETAILS_STEP.countryCode);

      let node = message;
      while (node && !node.contains(number)) node = node.parentElement;

      return Boolean(node && node.contains(code));
    };

    it('sits beneath the whole row, not under the number', () => {
      renderStep();

      expect(spansTheRow(screen.getByText(DETAILS_STEP.mobileHelp))).toBe(true);
    });

    it('spans the row when warning too', () => {
      renderStep({
        checks: { emailTaken: false, mobileTaken: true, mobileValid: true },
      });

      expect(spansTheRow(screen.getByText(DETAILS_STEP.mobileTaken))).toBe(
        true,
      );
    });
  });

  // PD-4684. The one field on this step whose options only the server knows: they
  // follow from the accounts chosen in step 1 and from whether the email's domain
  // carries an SSO rule.
  describe('the Authentication field', () => {
    const authenticationField = () =>
      screen.getByLabelText(DETAILS_STEP.authentication);

    it("carries the design's help line", () => {
      renderStep();

      expect(
        screen.getByText(DETAILS_STEP.authenticationHelp),
      ).toBeInTheDocument();
    });

    it('reports the chosen provider', () => {
      renderStep();

      fireEvent.click(authenticationField());
      fireEvent.click(screen.getByText('PCAR (Pass)'));

      expect(onChange).toHaveBeenCalledWith('ssoProvider', 'paccar');
    });

    // Required, so there is nothing to clear to. KeyDS shows the clear button once a
    // value is selected, and clearing emits an empty string.
    it('cannot be cleared once a choice is made', () => {
      renderStep({ draft: { ...DRAFT, ssoProvider: 'paccar' } });

      // KeyDS names it "Clear <label> value", so this cannot be a bare /clear/ —
      // the other selects on this form have clear buttons of their own.
      expect(
        screen.queryByRole('button', { name: /clear authentication value/i }),
      ).not.toBeInTheDocument();
    });

    it('offers password login', () => {
      renderStep();

      fireEvent.click(authenticationField());
      fireEvent.click(screen.getByText('Password'));

      expect(onChange).toHaveBeenCalledWith('ssoProvider', 'password');
    });

    // A domain rule cannot be overridden, so the field is not the admin's to
    // change — and a disabled required field with no reason given reads as a fault.
    describe('when an email domain rule decides', () => {
      const locked = {
        authentication: {
          assignment: 'domain',
          locked: true,
          provider: 'decisiv',
          requireSso: false,
        },
        authenticationOptions: [{ id: 'decisiv', label: 'Decisiv Okta' }],
        draft: { ...DRAFT, ssoProvider: 'decisiv' },
      };

      // A KeyDS Select does not put the HTML `disabled` attribute on its button:
      // InputField guards the container's click handler instead (see its
      // onInputContainerClickHandler). So what is checkable is the behaviour — the
      // list does not open, and nothing can be chosen.
      it('will not open, so the provider cannot be changed', () => {
        renderStep(locked);

        fireEvent.click(authenticationField());

        // The field renders its own value, so the label is on screen once whether
        // or not the list opened. A second copy would be the open list's option.
        expect(screen.queryAllByText('Decisiv Okta')).toHaveLength(1);
        expect(onChange).not.toHaveBeenCalled();
      });

      it('says which provider the domain requires, and that it cannot change', () => {
        renderStep(locked);

        expect(
          screen.getByText(DETAILS_STEP.authenticationLocked('Decisiv Okta')),
        ).toBeInTheDocument();
      });

      it('drops the generic help line', () => {
        renderStep(locked);

        expect(
          screen.queryByText(DETAILS_STEP.authenticationHelp),
        ).not.toBeInTheDocument();
      });
    });

    // Which providers are on offer follows from the email's domain, so there is
    // nothing to show until one is typed.
    describe('before an email has been entered', () => {
      const awaitingEmail = { isAskable: false, authenticationOptions: [] };

      it('will not open onto an empty list', () => {
        renderStep(awaitingEmail);

        fireEvent.click(authenticationField());

        expect(screen.queryByText('Password')).not.toBeInTheDocument();
        expect(onChange).not.toHaveBeenCalled();
      });

      it('asks for the email rather than reporting a failed lookup', () => {
        renderStep(awaitingEmail);

        expect(
          screen.getByText(DETAILS_STEP.authenticationNeedsEmail),
        ).toBeInTheDocument();
        expect(
          screen.queryByText(DETAILS_STEP.authenticationUnavailable),
        ).not.toBeInTheDocument();
      });
    });

    it('says so while the options are still being fetched', () => {
      renderStep({
        isAskable: true,
        isLoadingAuthentication: true,
        authenticationOptions: [],
      });

      expect(
        screen.getByText(DETAILS_STEP.authenticationLoading),
      ).toBeInTheDocument();
    });

    it('will not open while the options are still being fetched', () => {
      renderStep({
        isAskable: true,
        isLoadingAuthentication: true,
        authenticationOptions: AUTHENTICATION_OPTIONS,
      });

      fireEvent.click(authenticationField());

      expect(screen.queryByText('PCAR (Pass)')).not.toBeInTheDocument();
    });

    // An empty offer means the request never came back — useAuthenticationOptions
    // empties it when the fetch rejects, and the server's answer always holds at
    // least one option. An empty required select with no explanation is
    // unactionable, so the field says what went wrong instead.
    it('reports a lookup that never answered', () => {
      renderStep({ isAskable: true, authenticationOptions: [] });

      expect(
        screen.getByText(DETAILS_STEP.authenticationUnavailable),
      ).toBeInTheDocument();
    });

    it('will not open when the lookup never answered', () => {
      renderStep({ isAskable: true, authenticationOptions: [] });

      fireEvent.click(authenticationField());

      expect(onChange).not.toHaveBeenCalled();
    });

    it('shows what the server said about a rejected choice instead of the help', () => {
      renderStep({
        serverMessages: { ssoProvider: 'Authentication is not available' },
      });

      expect(
        screen.getByText('Authentication is not available'),
      ).toBeInTheDocument();
      expect(
        screen.queryByText(DETAILS_STEP.authenticationHelp),
      ).not.toBeInTheDocument();
    });
  });
});
